import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
    PackageMinus, Building2, Search, Loader2, RefreshCw, Send, Trash2, Plus,
} from 'lucide-react';

import CustomDatePicker from '../../components/CustomDatePicker';

import {
    fetchIssueCCList, fetchIndentsByCC, fetchIndentType, fetchIssueItemCodes,
    fetchTempDetails, saveTempItems, fetchCartGrid, deleteCartItem, saveIssue,
    clearIndentType, clearItemCodeList, clearTempItems, clearSaveTempResult, clearCartGrid, clearSubmitResult, resetNewStockIssue,
    selectNSICCList, selectNSIIndentList, selectNSIIndentType, selectNSIItemCodeList,
    selectNSITempItems, selectNSITempLoading,
    selectNSICartItems, selectNSICartLoading,
    selectNSILoading,
} from '../../slices/purchaseSlice/newStockIssueSlice';

// ── Shared UI (mirrors NewStockTransfer / OldStockIssue) ────────────────────

const inputCls =
    'w-full px-3 py-2 rounded-lg border text-sm bg-white dark:bg-gray-800 ' +
    'text-gray-800 dark:text-gray-100 border-gray-300 dark:border-gray-600 ' +
    'focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 ' +
    'disabled:opacity-60 disabled:cursor-not-allowed transition-colors';

const Label = ({ children, required }) => (
    <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-1">
        {children}{required && <span className="text-rose-500 ml-0.5">*</span>}
    </label>
);

const SectionCard = ({ icon: Icon, title, subtitle, children, accent = 'indigo' }) => {
    const colors = {
        indigo: 'from-indigo-50 to-indigo-100 dark:from-indigo-900/20 dark:to-indigo-800/20 border-indigo-200 dark:border-indigo-700 text-indigo-800 dark:text-indigo-300',
        amber:  'from-amber-50  to-amber-100  dark:from-amber-900/20  dark:to-amber-800/20  border-amber-200  dark:border-amber-700  text-amber-800  dark:text-amber-300',
    };
    const cls = colors[accent] || colors.indigo;
    return (
        <div className={`bg-gradient-to-br ${cls} rounded-xl border p-5 space-y-4`}>
            <div className="flex items-center gap-2">
                <Icon className="w-4 h-4" />
                <h3 className="font-semibold text-sm">{title}</h3>
                {subtitle && <span className="text-xs opacity-70 ml-1">{subtitle}</span>}
            </div>
            {children}
        </div>
    );
};

const SpinnerIcon = () => <Loader2 className="w-4 h-4 animate-spin" />;

const formatDateForSP = (d) => {
    if (!d) return '';
    const date = d instanceof Date ? d : new Date(d);
    if (isNaN(date.getTime())) return '';
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${String(date.getDate()).padStart(2, '0')}-${months[date.getMonth()]}-${date.getFullYear()}`;
};
const addDays = (date, days) => {
    const d = new Date(date);
    d.setDate(d.getDate() + days);
    return d;
};
const num = (v) => (isNaN(parseFloat(v)) ? 0 : parseFloat(v));

// menuData carries an optional prefillCC/prefillIndentNo — used when navigated here
// straight from an Indent Creation verification (Approve at the New Stock Issue level).
const NewStockIssue = ({ menuData }) => {
    const dispatch = useDispatch();
    const { userData } = useSelector((s) => s.auth);
    const currentUser = userData?.userName || userData?.employeeId || userData?.UID || 'system';
    const roleId = userData?.roleId || userData?.RID || '';
    const uid = userData?.UID || userData?.uid || '';

    const ccList = useSelector(selectNSICCList);
    const indentList = useSelector(selectNSIIndentList);
    const indentType = useSelector(selectNSIIndentType);
    const itemCodeList = useSelector(selectNSIItemCodeList);
    const tempItems = useSelector(selectNSITempItems);
    const tempLoading = useSelector(selectNSITempLoading);
    const cartItems = useSelector(selectNSICartItems);
    const cartLoading = useSelector(selectNSICartLoading);
    const loading = useSelector(selectNSILoading);

    const isAssetIndent = indentType === 'Asset';
    const isOtherIndent = indentType && indentType !== 'Asset';

    const [selectedCC, setSelectedCC] = useState('');
    const [selectedIndent, setSelectedIndent] = useState('');
    const [selectedItemCode, setSelectedItemCode] = useState('');

    const [tempQtyEdits, setTempQtyEdits] = useState({});
    const [cartRemarks, setCartRemarks] = useState({});
    const [cartChecked, setCartChecked] = useState({});

    const [issueDate, setIssueDate] = useState(new Date());
    const [tranDate, setTranDate] = useState(null);
    const [commonRemarks, setCommonRemarks] = useState('');
    const [submitted, setSubmitted] = useState(false);

    const prefillApplied = useRef(false);

    useEffect(() => {
        if (uid && roleId) dispatch(fetchIssueCCList({ uid, rid: roleId }));
        return () => dispatch(resetNewStockIssue());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ── Pre-fill from a verification-page redirect ──────────────────────────
    useEffect(() => {
        if (prefillApplied.current) return;
        if (!menuData?.prefillCC || ccList.length === 0) return;
        const match = ccList.find((c) => c.CCID === menuData.prefillCC);
        if (!match) return;
        prefillApplied.current = true;
        handleCCChange(menuData.prefillCC);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ccList, menuData]);

    useEffect(() => {
        if (!menuData?.prefillIndentNo || indentList.length === 0 || !selectedCC) return;
        const match = indentList.find((i) => i.Indentvalue === menuData.prefillIndentNo);
        if (!match || selectedIndent) return;
        handleIndentChange(menuData.prefillIndentNo);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [indentList, menuData, selectedCC]);

    const handleCCChange = (cc) => {
        setSelectedCC(cc);
        setSelectedIndent(''); setSelectedItemCode('');
        dispatch(clearIndentType());
        dispatch(clearItemCodeList());
        dispatch(clearTempItems());
        dispatch(clearCartGrid());
        setCartRemarks({}); setCartChecked({});
        if (cc) dispatch(fetchIndentsByCC(cc));
    };

    const handleIndentChange = (indent) => {
        setSelectedIndent(indent);
        setSelectedItemCode('');
        dispatch(clearItemCodeList());
        dispatch(clearTempItems());
        if (!indent) { dispatch(clearIndentType()); dispatch(clearCartGrid()); return; }
        dispatch(fetchIndentType(indent)).then((action) => {
            const type = action.payload;
            if (type && type !== 'Asset') {
                dispatch(fetchIssueItemCodes({ cccode: selectedCC, indentno: indent }));
            }
            dispatch(fetchCartGrid({ cccode: selectedCC, indentno: indent }));
        });
    };

    const refreshItemCodesAndCart = () => {
        if (isOtherIndent) dispatch(fetchIssueItemCodes({ cccode: selectedCC, indentno: selectedIndent }));
        dispatch(fetchCartGrid({ cccode: selectedCC, indentno: selectedIndent }));
    };

    const handleAddItem = () => {
        if (!selectedCC) { toast.warning('Select Cost Centre'); return; }
        if (!selectedIndent) { toast.warning('Select Indent No'); return; }
        if (!selectedItemCode) { toast.warning('Select Item Code'); return; }
        dispatch(fetchTempDetails({ itemcode: selectedItemCode, cccode: selectedCC, indent: selectedIndent }))
            .unwrap()
            .then((rows) => {
                if (!rows || rows.length === 0) toast.warning('No received stock available to issue for this Item Code');
            })
            .catch((e) => toast.error(typeof e === 'string' ? e : 'Failed to fetch item lot details'));
    };

    useEffect(() => {
        if (tempItems.length > 0) {
            const init = {};
            tempItems.forEach((r) => { init[r.TempId] = r.TempAssQty ?? '0.00'; });
            setTempQtyEdits(init);
        }
    }, [tempItems]);

    // Legacy (NewItemsIssueTempDetails.cshtml) only lets a lot row be edited when
    // (trade item code applies AND Action='Yes') or (no trade item code AND Action='No').
    const isRowEditable = (row) =>
        (row.TempTradeItemcode !== 'N/A' && row.Action === 'Yes') ||
        (row.TempTradeItemcode === 'N/A' && row.Action === 'No');

    // Legacy compares the footer totals formatted to 4 decimals — round the same way so
    // floating-point sums like 0.1 + 0.2 don't fail the exact-match check.
    const round4 = (v) => Math.round(v * 10000) / 10000;
    const tempTotals = useMemo(() => {
        const assigned = round4(tempItems.reduce((sum, r) => sum + num(tempQtyEdits[r.TempId]), 0));
        const accQty = round4(num(tempItems[tempItems.length - 1]?.ACCQty));
        return { assigned, accQty };
    }, [tempItems, tempQtyEdits]);

    const handleTempQtyChange = (row, value) => {
        // Legacy ValidateAmountQuantity: digits, max 3 decimals
        if (!/^\d*\.?\d{0,3}$/.test(value)) return;
        const avlQty = num(row.TempQuantity);
        const accQty = num(row.ACCQty);
        if (value !== '' && (num(value) > avlQty || num(value) > accQty)) {
            toast.error('Invalid Transfer Quantity');
            setTempQtyEdits((p) => ({ ...p, [row.TempId]: '' }));
            return;
        }
        setTempQtyEdits((p) => ({ ...p, [row.TempId]: value }));
    };

    // Legacy requires the assigned total to EXACTLY match the item's overall available qty
    // (not just <=) before it can be added to the issue cart.
    const handleAddToIssue = async () => {
        const qualifying = tempItems.filter((r) => num(tempQtyEdits[r.TempId]) > 0);
        if (qualifying.length === 0 || tempTotals.assigned === 0) { toast.error('Invalid Submission'); return; }
        if (tempTotals.assigned !== tempTotals.accQty) { toast.error('Invalid Quantity'); return; }

        const payload = {
            Trowids: qualifying.map((r) => r.TempId).join(',') + ',',
            TItemCodes: qualifying.map((r) => (r.TempItemcode || '').trim()).join(',') + ',',
            TNewQtys: qualifying.map((r) => num(tempQtyEdits[r.TempId])).join(',') + ',',
            Ttblnames: qualifying.map((r) => r.Tblname).join(',') + ',',
            TCCCode: selectedCC,
            // SP matches NewStockTransferCapitalTemp.ItemCode on this; legacy sends the row's TempItemcode
            TSingleItemcode: (qualifying[qualifying.length - 1].TempItemcode || selectedItemCode).trim(),
            Indentno: selectedIndent,
            TradeItemcodes: qualifying.map((r) => (r.TempTradeItemcode || 'N/A').trim()).join(',') + ',',
        };

        try {
            const result = await dispatch(saveTempItems(payload)).unwrap();
            const [status, code] = (result || '').split(',');
            if (status === 'Submited') {
                toast.success(`Item Code '${code}' Added Successfully`);
                setSelectedItemCode('');
                dispatch(clearTempItems());
                refreshItemCodesAndCart();
            } else {
                toast.error(result || 'Failed to add item');
            }
        } catch (error) {
            toast.error(typeof error === 'string' ? error : 'Error Occurred While Adding Items');
        } finally {
            dispatch(clearSaveTempResult());
        }
    };

    const handleCartCheck = (itemcode, checked) => setCartChecked((p) => ({ ...p, [itemcode]: checked }));
    const handleCartRemark = (itemcode, value) => setCartRemarks((p) => ({ ...p, [itemcode]: value }));

    const handleDeleteCartItem = async (row) => {
        if (!cartChecked[row.OItemcode]) { toast.error('Please Check for Delete'); return; }
        try {
            await dispatch(deleteCartItem({ DItemcode: row.OItemcode, Indentno: selectedIndent, TCCCode: selectedCC })).unwrap();
            toast.success('Deleted Successfully');
            refreshItemCodesAndCart();
        } catch (error) {
            toast.error(typeof error === 'string' ? error : 'Error Occurred While Deleting Item');
        }
    };

    const cartTotal = useMemo(() => num(cartItems[0]?.OSumTotal), [cartItems]);

    const handleIssueDateChange = (d) => {
        setIssueDate(d);
        setTranDate(null);
    };

    const handleReset = () => {
        dispatch(resetNewStockIssue());
        prefillApplied.current = false;
        setSelectedCC(''); setSelectedIndent(''); setSelectedItemCode('');
        setTempQtyEdits({}); setCartRemarks({}); setCartChecked({});
        setIssueDate(new Date()); setTranDate(null); setCommonRemarks(''); setSubmitted(false);
        if (uid && roleId) dispatch(fetchIssueCCList({ uid, rid: roleId }));
    };

    const handleSubmitIssue = async () => {
        if (!selectedCC) { toast.warning('Select Cost Centre'); return; }
        if (!selectedIndent) { toast.warning('Select Indent No'); return; }
        if (!issueDate) { toast.warning('Select Date'); return; }
        if (!tranDate) { toast.warning('Select Expected Recieved Date'); return; }
        if (!commonRemarks.trim()) { toast.warning('Enter Remarks'); return; }
        if (cartItems.length === 0) { toast.error('Please Add Items to Issue'); return; }

        const uncheckedCount = cartItems.filter((r) => !cartChecked[r.OItemcode]).length;
        if (uncheckedCount > 0) { toast.error('Please Verify Issue Item Codes'); return; }
        const missingRemarkCount = cartItems.filter((r) => !(cartRemarks[r.OItemcode] || '').trim()).length;
        if (missingRemarkCount > 0) { toast.error('Enter Item Remarks'); return; }

        const codes = cartItems.map((r) => r.OItemcode);
        if (new Set(codes).size !== codes.length) { toast.error('Duplicate Issue Item Code'); return; }

        const payload = {
            SItemcodes: cartItems.map((r) => r.OItemcode).join(',') + ',',
            SItemqtys: cartItems.map((r) => r.OItemqty).join(',') + ',',
            SItemamts: cartItems.map((r) => r.OItemamt).join(',') + ',',
            SCCCode: selectedCC,
            Indentno: selectedIndent,
            SDate: formatDateForSP(issueDate),
            TransactionDate: formatDateForSP(tranDate),
            SRemarks: commonRemarks.trim(),
            SIndRemarks: cartItems.map((r) => (cartRemarks[r.OItemcode] || '').trim()).join(',') + ',',
            RoleID: roleId,
            Createdby: currentUser,
        };

        try {
            const result = await dispatch(saveIssue(payload)).unwrap();
            const [status, refNo] = (result || '').split(',');
            if (status === 'Submited') {
                toast.success(`The Issue Ref No Is:- ${refNo} Generated Successfully`);
                setSubmitted(true);
            } else {
                toast.error(result || 'Failed to submit issue');
            }
        } catch (error) {
            toast.error(typeof error === 'string' ? error : 'Error Occurred While Items Issue');
        } finally {
            dispatch(clearSubmitResult());
        }
    };

    return (
        <div className="space-y-4 p-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                        <PackageMinus className="w-5 h-5 text-indigo-600" /> New Stock Issue
                    </h1>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Issue freshly received central stock against a raised indent</p>
                </div>
                <button onClick={handleReset}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 transition-colors">
                    <RefreshCw className="w-3.5 h-3.5" /> Reset
                </button>
            </div>

            <SectionCard icon={Building2} title="Cost Centre &amp; Indent">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <Label required>Issue To Cost Centre</Label>
                        <select className={inputCls} value={selectedCC} onChange={(e) => handleCCChange(e.target.value)} disabled={loading.ccList}>
                            <option value="">-Please Select-</option>
                            {ccList.map((c) => <option key={c.CCID} value={c.CCID}>{c.CCVAL}</option>)}
                        </select>
                    </div>
                    {selectedCC && (
                        <div>
                            <Label required>Indent No</Label>
                            <select className={inputCls} value={selectedIndent} onChange={(e) => handleIndentChange(e.target.value)} disabled={loading.indentList}>
                                <option value="">-Please Select-</option>
                                {indentList.map((i) => <option key={i.Indentvalue} value={i.Indentvalue}>{i.Indenttext}</option>)}
                            </select>
                        </div>
                    )}
                    {isOtherIndent && (
                        <div>
                            <Label required>Item Code</Label>
                            <select className={inputCls} value={selectedItemCode} onChange={(e) => setSelectedItemCode(e.target.value)}>
                                <option value="">-Please Select-</option>
                                {/* Value must be the bare item code (legacy uses ItemCodevalue) — the text is
                                    "Code , Name , Qty" and spGetNewItemsissueTempDetails' @Itemcode is varchar(20),
                                    so sending the text truncates it and no stock lots ever come back. */}
                                {itemCodeList.map((it, i) => <option key={`${it.ItemCodevalue}-${i}`} value={it.ItemCodevalue}>{it.ItemCodetext}</option>)}
                            </select>
                        </div>
                    )}
                </div>
                {isOtherIndent && (
                    <div className="text-right">
                        <button onClick={handleAddItem} disabled={tempLoading} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-semibold disabled:opacity-60 inline-flex items-center gap-1.5">
                            {tempLoading ? <SpinnerIcon /> : <Plus className="w-4 h-4" />} Add
                        </button>
                    </div>
                )}
            </SectionCard>

            {tempItems.length > 0 && (
                <SectionCard icon={Search} title="Add Items to Issue" accent="amber">
                    <div className="overflow-x-auto rounded-lg border border-amber-200 dark:border-amber-700">
                        <table className="min-w-full divide-y divide-amber-200 dark:divide-amber-700 text-xs">
                            <thead className="bg-amber-100/60 dark:bg-amber-900/30">
                                <tr>
                                    <th className="px-2 py-1.5 text-left font-semibold uppercase">Received Date</th>
                                    <th className="px-2 py-1.5 text-left font-semibold uppercase">Item Code</th>
                                    <th className="px-2 py-1.5 text-left font-semibold uppercase">MRR No</th>
                                    <th className="px-2 py-1.5 text-left font-semibold uppercase">PO No</th>
                                    <th className="px-2 py-1.5 text-right font-semibold uppercase">Quantity</th>
                                    <th className="px-2 py-1.5 text-right font-semibold uppercase">Assigned Qty</th>
                                    <th className="px-2 py-1.5 text-left font-semibold uppercase">Trade Item Code</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-amber-100 dark:divide-amber-800">
                                {tempItems.map((row) => (
                                    <tr key={row.TempId}>
                                        <td className="px-2 py-1 whitespace-nowrap">{row.Tempdate}</td>
                                        <td className="px-2 py-1 whitespace-nowrap">{row.TempItemcode}</td>
                                        <td className="px-2 py-1 whitespace-nowrap">{row.Tempmrr}</td>
                                        <td className="px-2 py-1 whitespace-nowrap">{row.Temppono}</td>
                                        <td className="px-2 py-1 text-right">{num(row.TempQuantity).toFixed(4)}</td>
                                        <td className="px-2 py-1 text-right">
                                            <input
                                                type="text"
                                                inputMode="decimal"
                                                className="w-24 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-xs text-right bg-white dark:bg-gray-700 disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:cursor-not-allowed"
                                                value={tempQtyEdits[row.TempId] ?? '0.00'}
                                                onFocus={(e) => e.target.select()}
                                                onChange={(e) => handleTempQtyChange(row, e.target.value)}
                                                disabled={!isRowEditable(row)}
                                            />
                                        </td>
                                        <td className="px-2 py-1 whitespace-nowrap">{row.TempTradeItemcode}</td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr className="bg-amber-100/60 dark:bg-amber-900/30 font-bold">
                                    <td className="px-2 py-1" colSpan={4}></td>
                                    <td className="px-2 py-1 text-right">{tempTotals.accQty.toFixed(4)}</td>
                                    <td className="px-2 py-1 text-right">{tempTotals.assigned.toFixed(4)}</td>
                                    <td className="px-2 py-1"></td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                    <div className="text-right">
                        <button onClick={handleAddToIssue} disabled={loading.saveTemp} className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-semibold disabled:opacity-60">
                            {loading.saveTemp ? 'Adding…' : 'Add To Issue'}
                        </button>
                    </div>
                </SectionCard>
            )}

            {selectedIndent && (
                <SectionCard icon={Building2} title="Item Code Details">
                    {cartLoading ? (
                        <div className="flex items-center justify-center gap-3 py-8"><SpinnerIcon /><span className="text-sm text-gray-500">Loading cart…</span></div>
                    ) : cartItems.length === 0 ? (
                        <p className="text-center text-sm text-gray-400 py-6">No items added to this issue yet.</p>
                    ) : (
                        <>
                            <div className="overflow-x-auto rounded-lg border border-indigo-200 dark:border-indigo-700">
                                <table className="min-w-full divide-y divide-indigo-200 dark:divide-indigo-700 text-xs">
                                    <thead className="bg-indigo-100/60 dark:bg-indigo-900/30">
                                        <tr>
                                            <th className="px-2 py-1.5 text-center font-semibold uppercase">Chk</th>
                                            <th className="px-2 py-1.5 text-left font-semibold uppercase">Item Code</th>
                                            <th className="px-2 py-1.5 text-left font-semibold uppercase">Item Name</th>
                                            <th className="px-2 py-1.5 text-left font-semibold uppercase">Specification</th>
                                            <th className="px-2 py-1.5 text-left font-semibold uppercase">Units</th>
                                            <th className="px-2 py-1.5 text-right font-semibold uppercase">Quantity</th>
                                            <th className="px-2 py-1.5 text-left font-semibold uppercase">Remarks</th>
                                            <th className="px-2 py-1.5 text-right font-semibold uppercase">Amount</th>
                                            <th className="px-2 py-1.5 text-center font-semibold uppercase">Delete</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-indigo-100 dark:divide-indigo-800">
                                        {cartItems.map((row) => (
                                            <tr key={row.OItemcode}>
                                                <td className="px-2 py-1 text-center"><input type="checkbox" checked={!!cartChecked[row.OItemcode]} onChange={(e) => handleCartCheck(row.OItemcode, e.target.checked)} /></td>
                                                <td className="px-2 py-1 whitespace-nowrap font-medium">{row.OItemcode}</td>
                                                <td className="px-2 py-1">{row.OItemname}</td>
                                                <td className="px-2 py-1">{row.OItemspecification}</td>
                                                <td className="px-2 py-1 whitespace-nowrap">{row.OUnits}</td>
                                                <td className="px-2 py-1 text-right">{num(row.OItemqty).toFixed(4)}</td>
                                                <td className="px-2 py-1">
                                                    <textarea rows={1} className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-xs bg-white dark:bg-gray-700"
                                                        value={cartRemarks[row.OItemcode] || ''} onChange={(e) => handleCartRemark(row.OItemcode, e.target.value)} />
                                                </td>
                                                <td className="px-2 py-1 text-right">{num(row.OItemamt).toFixed(2)}</td>
                                                <td className="px-2 py-1 text-center">
                                                    <button onClick={() => handleDeleteCartItem(row)} className="px-2 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-[11px] flex items-center gap-1 mx-auto">
                                                        <Trash2 className="w-3 h-3" /> Delete
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot>
                                        <tr className="bg-gray-100 dark:bg-gray-900/50 font-bold">
                                            <td colSpan={7}></td>
                                            <td className="px-2 py-1 text-right">{cartTotal.toFixed(2)}</td>
                                            <td></td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2">
                                <div>
                                    <Label required>Issue Date</Label>
                                    <CustomDatePicker value={issueDate} onChange={handleIssueDateChange} format="DD-MMM-YYYY" placeholder="Select Date" maxDate={new Date()} />
                                </div>
                                <div>
                                    <Label required>Expected Recieved Date</Label>
                                    <CustomDatePicker value={tranDate} onChange={setTranDate} format="DD-MMM-YYYY" placeholder="Select Date"
                                        minDate={issueDate || undefined} maxDate={issueDate ? addDays(issueDate, 7) : undefined} disabled={!issueDate} />
                                </div>
                                <div>
                                    <Label required>Remarks</Label>
                                    <textarea rows={2} className={inputCls} value={commonRemarks} onChange={(e) => setCommonRemarks(e.target.value)} />
                                </div>
                            </div>

                            <div className="text-right">
                                <button onClick={handleSubmitIssue} disabled={loading.submit || submitted} className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-semibold disabled:opacity-60 inline-flex items-center gap-1.5">
                                    <Send className="w-4 h-4" /> {loading.submit ? 'Submitting…' : 'Submit'}
                                </button>
                            </div>
                        </>
                    )}
                </SectionCard>
            )}
        </div>
    );
};

export default NewStockIssue;
